import { Editor } from "@tiptap/core";
import { defer } from "lodash";
import compact from "lodash/compact";

import { getLabel } from "@apps/use-label/use-label";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";

import { getTipTapCreatedByUser } from "../helpers";
import {
  embedUrlCommandItem,
  explorerCommandItem,
  findCommandItem,
} from "./commands-helpers";
import { icons } from "./commands-icons";

const focusDeleteRange = (editor: Editor, range: any) =>
  editor.chain().focus().deleteRange(range);

type CommandItem = {
  id: string;
  title: string;
  keywords?: string;
  icon?: any;
  searchFilters?: any;
  command?: (args: { editor: Editor; range: any; props?: any }) => void;
};

type CommandSection = {
  id: string;
  title: string;
  subItems: CommandItem[];
};

export const getSuggestionItems =
  ({ permissionGoal }: { permissionGoal: boolean }) =>
  ({ query }: { query: string }) => {
    const l = getLabel();
    const currentOrganization = currentOrganizationVar();
    const currentUser = currentUserVar();

    const explorerCommandWithLogic: CommandItem = {
      ...explorerCommandItem,
      command: ({ editor, range }) => {
        focusDeleteRange(editor, range)
          .addEmptyExplorer({
            creatorName: currentUser.name,
            creatorId: currentUser.id,
          })
          .run();
      },
    };

    const actionItem: CommandItem | null = currentOrganization.featureFlags
      .actionItems
      ? {
          id: "action-item",
          title: "Action item",
          keywords: "task todo",
          icon: icons.actionItem,
          command: ({ editor, range }) => {
            editor.chain().focus().run();
            defer(() => {
              focusDeleteRange(editor, range)
                .insertActionItem({
                  createdByUser: getTipTapCreatedByUser(currentUser),
                  title: "",
                })
                .focus()
                .run();
            });
          },
        }
      : null;

    const goalItem: CommandItem | null = currentOrganization.featureFlags.goals
      ? {
          id: "goal",
          title: `${l("goal", { capitalize: true })}${
            permissionGoal ? "" : " (upgrade)"
          }`,
          keywords: "objective okr",
          icon: icons.goal,
          command: ({ editor, range }) => {
            if (!permissionGoal) {
              document.location.href = "/billing";
              return;
            }
            editor.chain().focus().run();
            defer(() => {
              focusDeleteRange(editor, range)
                .insertGoal({
                  createdByUser: getTipTapCreatedByUser(currentUser),
                  title: "",
                })
                .run();
            });
          },
        }
      : null;

    const recognitionItem: CommandItem | null = currentOrganization.featureFlags
      .recognitions
      ? {
          id: "recognition",
          title: l("recognition", { capitalize: true }),
          keywords: "kudos praise",
          icon: icons.recognition,
          command: ({ editor, range }) => {
            focusDeleteRange(editor, range)
              .insertRecognition({
                createdByUser: getTipTapCreatedByUser(currentUser),
                title: "",
              })
              .run();
          },
        }
      : null;

    const kpiItem: CommandItem | null = currentOrganization.featureFlags.kpis
      ? {
          id: "kpi",
          title: "KPI",
          keywords: "metric",
          icon: icons.kpi,
          command: ({ editor, range }) => {
            focusDeleteRange(editor, range)
              .insertKPIEmbed({
                createdByUser: getTipTapCreatedByUser(currentUser),
              })
              .run();
          },
        }
      : null;

    const kpiGroupItem: CommandItem | null = currentOrganization.featureFlags
      .kpis
      ? {
          id: "kpi-group",
          title: "KPI group",
          keywords: "metric",
          icon: icons.kpiGroup,
          command: ({ editor, range }) => {
            focusDeleteRange(editor, range)
              .insertKPIGroupEmbed({
                createdByUser: getTipTapCreatedByUser(currentUser),
              })
              .run();
          },
        }
      : null;

    const findItem: CommandItem | null = currentOrganization.featureFlags
      .explorer
      ? findCommandItem
      : null;

    const explorerItem: CommandItem | null = currentOrganization.featureFlags
      .explorer
      ? explorerCommandWithLogic
      : null;

    const textItems: CommandItem[] = [
      {
        id: "h1",
        title: "Heading 1",
        keywords: "h1 title",
        icon: icons.h1,
        command: ({ editor, range }) => {
          focusDeleteRange(editor, range)
            .setNode("heading", { level: 1 })
            .run();
        },
      },
      {
        id: "h2",
        title: "Heading 2",
        keywords: "h2 subtitle",
        icon: icons.h2,
        command: ({ editor, range }) => {
          focusDeleteRange(editor, range)
            .setNode("heading", { level: 2 })
            .run();
        },
      },
      {
        id: "h3",
        title: "Heading 3",
        keywords: "h3",
        icon: icons.h3,
        command: ({ editor, range }) => {
          focusDeleteRange(editor, range)
            .setNode("heading", { level: 3 })
            .run();
        },
      },
      {
        id: "bullet-list",
        title: "Bullet list",
        keywords: "ul unordered",
        icon: icons.bulletList,
        command: ({ editor, range }) => {
          focusDeleteRange(editor, range).toggleBulletList().run();
        },
      },
      {
        id: "ordered-list",
        title: "Numbered list",
        keywords: "ol ordered",
        icon: icons.orderedList,
        command: ({ editor, range }) => {
          focusDeleteRange(editor, range).toggleOrderedList().run();
        },
      },
      {
        id: "table",
        title: "Table",
        icon: icons.table,
        command: ({ editor, range }) => {
          focusDeleteRange(editor, range).insertTable().run();
        },
      },
    ];

    const insertItems: CommandItem[] = compact([
      embedUrlCommandItem,
      findItem,
      explorerItem,
      actionItem,
      recognitionItem,
      goalItem,
      kpiItem,
      kpiGroupItem,
      {
        id: "button",
        title: "Button",
        icon: icons.button,
        command: ({ editor, range }: { editor: Editor; range: any }) => {
          focusDeleteRange(editor, range)
            .addButton({
              createdByUser: getTipTapCreatedByUser(currentUser),
            })
            .run();
        },
      } as CommandItem,
      {
        id: "timer",
        title: "Timer",
        icon: icons.timer,
        command: ({ editor, range }: { editor: Editor; range: any }) => {
          focusDeleteRange(editor, range).addTimer().run();
        },
      } as CommandItem,
    ]);

    const sections: CommandSection[] = [
      {
        id: "basics-headline",
        title: "Basics",
        subItems: textItems,
      },
      {
        id: "insert-headline",
        title: "Insert",
        subItems: insertItems,
      },
    ];

    const matches = (item: CommandItem, q: string) => {
      const haystack = `${item.title} ${item.keywords || ""}`.toLowerCase();
      return haystack.includes(q);
    };

    const lowerQuery = query.toLowerCase().trim();

    // When the user is typing, return a flat ranked list with no section
    // headers — items whose title starts with the query rank first.
    if (lowerQuery) {
      const allItems = sections.flatMap((section) => section.subItems);
      const matched = allItems.filter((item) => matches(item, lowerQuery));
      matched.sort((a, b) => {
        const aStarts = a.title.toLowerCase().startsWith(lowerQuery) ? 0 : 1;
        const bStarts = b.title.toLowerCase().startsWith(lowerQuery) ? 0 : 1;
        return aStarts - bStarts;
      });
      return matched;
    }

    // No query: show grouped output with section headers.
    return sections.reduce(
      (memo, section) => [...memo, section, ...section.subItems],
      [] as Array<CommandItem | CommandSection>,
    );
  };
